/*###########################################################################
// Responsive video
//#########################################################################*/

import { throttle, debounce } from "../utilities/_throttleDebounce"
import { inViewport } from "../utilities/_inViewport"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
gsap.registerPlugin(ScrollTrigger)

const responsiveVideo = {
	responsiveVideos: null,

	breakpoints: {
		mobile: 480,
		tablet: 768,
	},

	init: function () {
		const self = this

		// Set local elements
		self.responsiveVideos = document.querySelectorAll('.responsive-video')

		if (self.responsiveVideos !== null) {
			self.responsiveVideos.forEach(function (responsiveVideo, index) {
				self.handleVideoSource(responsiveVideo, false)

				if (responsiveVideo.parentNode.querySelector('.responsive-video__play') !== null) {
					self.handlePlay(responsiveVideo)
				}

				if (responsiveVideo.parentNode.querySelector('.responsive-video__sound') !== null) {
					self.handleSound(responsiveVideo)
				}
			})

			self.handleResize()
		}
	},

	handlePlay(responsiveVideo) {
		const self = this

		const playButton = responsiveVideo.parentNode.querySelector('.responsive-video__play')
		const noControls = responsiveVideo.hasAttribute('no-controls') ? true : false

		if (responsiveVideo.hasAttribute('autoplay')) {
			if (!noControls) {
				responsiveVideo.setAttribute('controls', 'controls')
			}

			playButton.classList.add('hidden')
		}

		playButton.addEventListener('click', function () {
			if (!noControls) {
				responsiveVideo.setAttribute('controls', 'controls')
			}

			responsiveVideo.play()

			playButton.classList.add('hidden')
		})

		responsiveVideo.addEventListener('pause', function () {
			if (responsiveVideo.seeking) {
				return
			}

			if (!noControls) {
				responsiveVideo.removeAttribute('controls')
			}

			playButton.classList.remove('hidden')
		})
	},

	handleSound(responsiveVideo) {
		const self = this

		const soundButton = responsiveVideo.parentNode.querySelector('.responsive-video__sound')
		const soundButtonText = soundButton.querySelector('.responsive-video__sound__text')

		if (soundButtonText.textContent === 'Mute') {
			soundButton.classList.add('responsive-video__sound--on')
		}

		soundButton.addEventListener('click', function() {
			self.toggleSound(responsiveVideo, soundButton, soundButtonText)
		})
	},

	toggleSound(responsiveVideo, soundButton, soundButtonText) {
		const isMuted = responsiveVideo.muted

		if (isMuted) {
			responsiveVideo.muted = false
			soundButton.classList.add('responsive-video__sound--on')
			soundButtonText.textContent = soundButtonText.dataset.activeText
		} else {
			responsiveVideo.muted = true
			soundButton.classList.remove('responsive-video__sound--on')
			soundButtonText.textContent = soundButtonText.dataset.inactiveText
		}
	},

	handleVideoSource: function (responsiveVideo, isResize) {
		const self = this

		const video = responsiveVideo

		if (responsiveVideo.classList.contains('responsive-video--has-init')) {
			return
		}
		responsiveVideo.classList.add('responsive-video--has-init')

		if (video.classList.contains('responsive-video--lazy') && isResize !== true) {
			const inViewportFunc = () => {
				if (inViewport(video, -(window.innerHeight / 5)) && !video.classList.contains('responsive-video--lazyloaded')) {
					video.classList.add('responsive-video--lazyloaded')

					if (window.innerWidth <= self.breakpoints.mobile && (!video.hasAttribute('data-is-small') || video.getAttribute('data-is-small') == 'false')) {
						if (video.hasAttribute("data-video-src-sm")) {
							self.setSmallVideoSource(video)
						} else if (video.hasAttribute("data-video-src-md")) {
							self.setMediumVideoSource(video)
						} else {
							self.setLargeVideoSource(video)
						}
					}
					// If above mobile
					else if (window.innerWidth > self.breakpoints.mobile && window.innerWidth <= self.breakpoints.tablet && (!video.hasAttribute('data-is-medium') || video.getAttribute('data-is-medium') == 'false')) {
						if (video.hasAttribute("data-video-src-md")) {
							self.setMediumVideoSource(video)
						} else {
							self.setLargeVideoSource(video)
						}
					}
					// If above desktop
					else if (window.innerWidth > self.breakpoints.tablet && (!video.hasAttribute('data-is-large') || video.getAttribute('data-is-large') == 'false')) {
						self.setLargeVideoSource(video)
					}

					if (window.innerWidth <= self.breakpoints.tablet) {
						video.setAttribute('data-is-small', true)
						video.setAttribute('data-is-large', false)
					} else {
						video.setAttribute('data-is-small', false)
						video.setAttribute('data-is-large', true)
					}
				}
			}
			inViewportFunc()

			window.addEventListener('scroll', throttle(function () {
				inViewportFunc()
			}, 200))
		} else {
			if (window.innerWidth <= self.breakpoints.mobile && (!video.hasAttribute('data-is-small') || video.getAttribute('data-is-small') == 'false')) {
				if (video.hasAttribute("data-video-src-sm")) {
					self.setSmallVideoSource(video)
				} else if (video.hasAttribute("data-video-src-md")) {
					self.setMediumVideoSource(video)
				} else {
					self.setLargeVideoSource(video)
				}
			}
			// If above mobile
			else if (window.innerWidth > self.breakpoints.mobile && window.innerWidth <= self.breakpoints.tablet && (!video.hasAttribute('data-is-medium') || video.getAttribute('data-is-medium') == 'false')) {
				if (video.hasAttribute("data-video-src-md")) {
					self.setMediumVideoSource(video)
				} else {
					self.setLargeVideoSource(video)
				}
			}
			// If above desktop
			else if (window.innerWidth > self.breakpoints.tablet && (!video.hasAttribute('data-is-large') || video.getAttribute('data-is-large') == 'false')) {
				self.setLargeVideoSource(video)
			}

			if (window.innerWidth <= self.breakpoints.mobile) {
				video.setAttribute('data-is-small', true)
				video.setAttribute('data-is-medium', false)
				video.setAttribute('data-is-large', false)
			} else if (window.innerWidth > self.breakpoints.mobile && window.innerWidth <= self.breakpoints.tablet) {
				video.setAttribute('data-is-small', false)
				video.setAttribute('data-is-medium', true)
				video.setAttribute('data-is-large', false)
			} else {
				video.setAttribute('data-is-small', false)
				video.setAttribute('data-is-medium', false)
				video.setAttribute('data-is-large', true)
			}
		}

	},

	setLargeVideoSource: function (video) {
		const self = this

		// Set poster image
		//
		if (video.hasAttribute("data-video-poster-lg")) {
			video.setAttribute("poster", video.getAttribute("data-video-poster-lg"))
		}

		// Create sources
		//
		let sources = "<source src='" + video.getAttribute("data-video-src-lg") + "'>"

		// Reload the video with new sources
		//
		video.innerHTML = sources
		video.load()

		// Handle hover videos
		//
		self.handleHoverVideo(video)

		// Set screen size state of this video
		//
		video.setAttribute('data-is-small', false)
		video.setAttribute('data-is-medium', false)
		video.setAttribute('data-is-large', true)
	},

	setMediumVideoSource: function (video) {
		const self = this

		// Set poster image
		//
		if (video.hasAttribute('data-video-poster-md')) {
      video.setAttribute('poster', video.getAttribute('data-video-poster-md'))
    } else if (video.hasAttribute('data-video-poster-lg')) {
      video.setAttribute('poster', video.getAttribute('data-video-poster-lg'))
    }

		// Create source
		//
		let sources = "<source src='" + video.getAttribute("data-video-src-md") + "'>"

		// Reload the video with new sources
		//
		video.innerHTML = sources
		video.load()

		// Handle hover videos
		//
		self.handleHoverVideo(video)

		// Set screen size state of this video
		//
		video.setAttribute('data-is-small', false)
		video.setAttribute('data-is-medium', true)
		video.setAttribute('data-is-large', false)
	},

	setSmallVideoSource: function (video) {
		const self = this

		// Set poster image
		//
		if (video.hasAttribute('data-video-poster-sm')) {
      video.setAttribute('poster', video.getAttribute('data-video-poster-sm'))
    } else if (video.hasAttribute('data-video-poster-md')) {
      video.setAttribute('poster', video.getAttribute('data-video-poster-md'))
    } else if (video.hasAttribute('data-video-poster-lg')) {
      video.setAttribute('poster', video.getAttribute('data-video-poster-lg'))
    }

		// Create source
		//
		let sources = "<source src='" + video.getAttribute("data-video-src-sm") + "'>"

		// Reload the video with new sources
		//
		video.innerHTML = sources
		video.load()

		// Handle hover videos
		//
		self.handleHoverVideo(video)

		// Set screen size state of this video
		//
		video.setAttribute('data-is-small', true)
		video.setAttribute('data-is-medium', false)
		video.setAttribute('data-is-large', false)
	},

	handleHoverVideo(video) {
		const self = this

		const videoInSlider = video.closest('.swiper-slide') !== null ? true : false

		if (video.classList.contains('responsive-video--hover')) {
			const closestAnchorOrButton = video.closest('a, button')
			if (closestAnchorOrButton !== null) {
				video.pause()

				let pauseTimeout = null

				closestAnchorOrButton.addEventListener('mouseenter', function () {
					if (window.innerWidth <= self.breakpoints.mobile) {
						return
					}

					clearTimeout(pauseTimeout)

					video.play()
					video.style.opacity = 1
				})

				closestAnchorOrButton.addEventListener('mouseleave', function () {
					if (window.innerWidth <= self.breakpoints.mobile) {
						return
					}

					clearTimeout(pauseTimeout)

					video.style.opacity = 0

					pauseTimeout = setTimeout(() => {
						video.pause()
						video.currentTime = 0
					}, 300)
				})

				if (!videoInSlider) {
					ScrollTrigger.create({
						trigger: video,
						start: 'center bottom-=200',
						end: 'center top+=200',
						onEnter: () => {
							if (window.innerWidth > self.breakpoints.mobile) {
								return
							}

							clearTimeout(pauseTimeout)

							video.play()
							video.style.opacity = 1
						},
						onEnterBack: () => {
							if (window.innerWidth > self.breakpoints.mobile) {
								return
							}

							clearTimeout(pauseTimeout)

							video.play()
							video.style.opacity = 1
						},
						onLeave: () => {
							if (window.innerWidth > self.breakpoints.mobile) {
								return
							}

							video.style.opacity = 0

							pauseTimeout = setTimeout(() => {
								video.pause()
								video.currentTime = 0
							}, 300)
						},
						onLeaveBack: () => {
							if (window.innerWidth > self.breakpoints.mobile) {
								return
							}

							video.style.opacity = 0

							pauseTimeout = setTimeout(() => {
								video.pause()
								video.currentTime = 0
							}, 300)
						},
					})
				} else {
					const onScroll = throttle(function () {
						if (window.innerWidth > self.breakpoints.mobile) {
							return
						}

						if (inViewport(video, -(window.innerHeight / 5))) {
							const slideIsActive = video.closest('.swiper-slide').classList.contains('swiper-slide-active')

							if (slideIsActive) {
								video.play()
								video.style.opacity = 1

								window.removeEventListener('scroll', onScroll)
							}
						}
					}, 200)

					window.addEventListener('scroll', onScroll)

					const onSliderUpdate = () => {
						const slideIsActive = video.closest('.swiper-slide').classList.contains('swiper-slide-active')

						if (slideIsActive) {
							clearTimeout(pauseTimeout)

							video.play()
							video.style.opacity = 1
						} else {
							video.style.opacity = 0

							pauseTimeout = setTimeout(() => {
								video.pause()
								video.currentTime = 0
							}, 300)
						}
					}

					window.addEventListener('sliderCaseStudiesUpdate', () => {
						if (window.innerWidth > self.breakpoints.mobile) {
							return
						}

						setTimeout(() => {
							onSliderUpdate()
						}, 250)
					})

					window.addEventListener('sliderTeamCardsUpdate', () => {
						if (window.innerWidth > self.breakpoints.mobile) {
							return
						}

						setTimeout(() => {
							onSliderUpdate()
						}, 250)
					})
				}
			}
		}
	},

	handleResize: function () {
		const self = this

		self.responsiveVideos.forEach(function (responsiveVideo, index) {

			let width = window.innerWidth

			window.addEventListener("resize", debounce(function () {
				if (window.innerWidth !== width) {
					width = window.innerWidth

					self.handleVideoSource(responsiveVideo, true)
				}
			}, 500))
		})
	}
};

initComponent(() => {
	responsiveVideo.init()
})

window.addEventListener('modalContentLoaded', () => {
	setTimeout(() => {
		responsiveVideo.init()
	}, 100)
})

if (typeof htmx !== 'undefined') {
	// After swap re-init
	//
	htmx.on('htmx:afterSwap', function (event) {
		setTimeout(() => {
			responsiveVideo.init()
		}, 500)
	})
}
